import { useState, useEffect, useRef, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { orpc } from '../lib/orpc.js';
import { current_language } from '../i18n/index.js';
import { format_inr } from '../lib/format.js';
import { friendly_error, is_slot_taken_error } from '../lib/friendly-error.js';
import { LEDGER_KIND, LEDGER_GLYPH, ledger_status_label, ledger_month_label } from '../lib/ledger-format.js';
import { MarkPaidModal } from '../components/MarkPaidModal.js';
import { ImportScheduleModal } from '../components/ImportScheduleModal.js';
import { Icon } from '../components/Icon.js';
import { NotifyMenu } from '../components/NotifyMenu.js';
import { FullscreenWrap, FullscreenToggle } from '../components/FullscreenSection.js';
import { useAuth } from '../auth.js';

interface Subscriber {
  id: string;
  name_ta: string;
  name_en: string;
  phone: string;
}
interface Chit {
  id: string;
  name_ta: string;
  name_en: string;
  pool_size: number;
  duration_months: number;
  monthly_subscription_paise: number;
  foreman_commission_pct: number;
  collection_day: number;
  start_date: string;
  status: string;
}
interface Cell {
  due_month: string;
  subscription_id: string | null;
  status: 'pending' | 'paid' | 'partial' | 'overdue' | 'no_row';
  expected_amount_paise: number;
  total_paid_paise: number;
}
interface MatrixRow {
  subscriber: Subscriber | null;
  slot: number;
  cells: Cell[];
}
interface MembershipRow {
  id: string;
  slot: number;
  subscriber: Subscriber | null;
}

type ChitTab = 'overview' | 'ledger' | 'schedule' | 'transactions';

interface CyclePlanRow {
  id: string;
  chit_id: string;
  cycle_index: number;
  cycle_date: string | Date;
  pay_amount_paise: number;
  receive_amount_paise: number;
  recipient_subscriber_id: string | null;
  notes?: string;
}

interface CellPop {
  x: number;
  y: number;
  slot: number;
  name: string;
  phone: string | null;
  subscriber_id: string | null;
  due_month: string;
  status: Cell['status'];
  subscription_id: string | null;
  expected: number;
  paid: number;
}

function format_date_dmy(input: string | Date): string {
  const d = typeof input === 'string' ? new Date(input) : input;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

export function ChitDetailPage(): ReactElement {
  const { t } = useTranslation();
  const lang = current_language();
  const params = useParams<{ chit_id: string }>();
  const chit_id = params.chit_id ?? '';
  const { session } = useAuth();
  const is_admin = !!session?.scopes.includes('admin');
  const [pay_target, set_pay_target] = useState<{
    subscription_id: string;
    expected: number;
    paid: number;
    name: string;
    subscriber_id?: string;
  } | null>(null);
  const [tab, set_tab] = useState<ChitTab>('overview');
  const [show_edit_chit, set_show_edit_chit] = useState(false);
  const [inline_add, set_inline_add] = useState<{ cycle_index: number } | null>(null);
  // Ledger fullscreen state lives here (parent) because the ledger card is
  // rendered inline in ChitDetailPage. The schedule card lives inside
  // TimetableTab and owns its own fullscreen state — same idea, local scope.
  const [is_ledger_fs, set_is_ledger_fs] = useState(false);
  const qc_top = useQueryClient();

  const publish_m = useMutation({
    mutationFn: () => orpc.chits.publish({ chit_id }),
    onSuccess: () => {
      void qc_top.invalidateQueries({ queryKey: ['ledger-matrix', chit_id] });
    },
  });

  // Ledger cell popover + on-demand dues generation. A future-dated chit has
  // no SubscriptionLedger rows yet, so its cells are all `no_row`; generating
  // that month's dues populates the column and makes the cells actionable.
  const [cell_pop, set_cell_pop] = useState<CellPop | null>(null);
  const gen_m = useMutation({
    mutationFn: (target_month: string) => orpc.ledger.generate_cycle({ chit_id, target_month }),
    onSuccess: () => {
      void qc_top.invalidateQueries({ queryKey: ['ledger-matrix', chit_id] });
      set_cell_pop(null);
    },
  });

  const q = useQuery({
    queryKey: ['ledger-matrix', chit_id],
    queryFn: () => orpc.ledger.get_matrix({ chit_id }),
    enabled: !!chit_id,
  });

  const mem_q = useQuery({
    queryKey: ['memberships', chit_id],
    queryFn: () => orpc.memberships.list_by_chit({ chit_id }),
    enabled: !!chit_id,
  });
  const memberships = (mem_q.data?.memberships ?? []) as MembershipRow[];

  // Cycle plan powers the Overview payout-snapshot KPIs. Same query key as the
  // Payout Schedule tab, so react-query dedupes — one fetch, shared cache.
  const plan_q = useQuery({
    queryKey: ['cycle-plan', chit_id],
    queryFn: () => orpc.chits.get_cycle_plan({ chit_id }),
    enabled: !!chit_id,
  });
  const plan = (plan_q.data?.rows ?? []) as CyclePlanRow[];

  if (q.isLoading) return <div className="text-sm text-ink/60">Loading…</div>;
  if (q.error) return <div className="text-sm text-bad">{friendly_error(q.error)}</div>;
  if (!q.data) return <></>;

  const chit = q.data.chit as Chit;
  const months = q.data.months as string[];
  const rows = q.data.rows as MatrixRow[];

  // Pre-publish validation: every cycle must have a recipient before the chit
  // goes live. In draft the ledger is shown but disabled (overlay) and Publish
  // is gated on full assignment.
  const is_draft = chit.status === 'draft';
  const status_label = ((): string => {
    const map: Record<string, [string, string]> = {
      draft: ['வரைவு', 'Draft'],
      active: ['வெளியிடப்பட்டது', 'Published'],
      completed: ['முடிந்தது', 'Completed'],
      cancelled: ['ரத்து', 'Cancelled'],
    };
    const pair = map[chit.status];
    return pair ? (lang === 'ta' ? pair[0] : pair[1]) : chit.status;
  })();
  const total_cycles = plan.length;
  const unassigned_cycles = plan.filter((p) => !p.recipient_subscriber_id).map((p) => p.cycle_index);
  const assigned_count = total_cycles - unassigned_cycles.length;
  const all_assigned = total_cycles > 0 && unassigned_cycles.length === 0;

  const status_pill =
    chit.status === 'draft'
      ? 'bg-warn-soft text-warn'
      : chit.status === 'active'
        ? 'bg-good-soft text-good'
        : chit.status === 'cancelled'
          ? 'bg-bad-soft text-bad'
          : 'bg-bg-elev text-ink/60';

  return (
    <div className="page full">
      <div className="page-head">
        <div>
          <Link to="/chits" style={{ color: 'var(--ink-3)', textDecoration: 'none', fontSize: 12, display: 'inline-block', marginBottom: 6 }}>
            ← {lang === 'ta' ? 'சீட்டுகள்' : 'Chits'}
          </Link>
          <div className={'h-title ' + (lang === 'ta' ? 'tamil' : '')}>
            {lang === 'ta' ? chit.name_ta : chit.name_en}
          </div>
          <div className="h-sub" style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
            <span>{chit.pool_size} {t('chit_detail.members', 'members')}</span>
            <span>·</span>
            <span>{chit.duration_months} {t('chit_detail.months', 'months')}</span>
            <span>·</span>
            <span className="mono">{format_inr(chit.monthly_subscription_paise)}/mo</span>
            <span>·</span>
            <span>{chit.foreman_commission_pct}% commission</span>
            <span>·</span>
            <span>Day {chit.collection_day}</span>
            <span className={`uppercase tracking-wider text-[10px] px-2 py-0.5 rounded ${status_pill}`}>
              {status_label}
            </span>
          </div>
        </div>
        {is_admin && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {is_draft && !all_assigned && (
              <span style={{ fontSize: 11.5, color: 'var(--warn)', maxWidth: 200, textAlign: 'right', lineHeight: 1.25 }}>
                {t(
                  `${unassigned_cycles.length} கட்டம் ஒதுக்கப்படவில்லை — வெளியிட அனைத்தையும் ஒதுக்கவும்`,
                  `${unassigned_cycles.length} instalment${unassigned_cycles.length === 1 ? '' : 's'} unassigned — assign all to publish`,
                )}
              </span>
            )}
            {is_draft && (
              <button
                className="btn primary"
                title={all_assigned ? undefined : `Assign all ${total_cycles} instalments before publishing`}
                onClick={() => {
                  if (window.confirm('Publish this chit? After publishing, pool size, duration, and start date can no longer be changed.')) {
                    publish_m.mutate();
                  }
                }}
                disabled={publish_m.isPending || !all_assigned}
              >
                <Icon.check /> {publish_m.isPending ? 'Publishing…' : 'Publish'}
              </button>
            )}
            <button className="btn" onClick={() => set_show_edit_chit(true)}>
              {t('chit_detail.edit', 'Edit chit')}
            </button>
          </div>
        )}
      </div>
      {publish_m.error && (
        <div className="mb-3 text-xs text-bad bg-bad-soft px-3 py-2 rounded">
          {friendly_error(publish_m.error)}
        </div>
      )}

      <ChitWhatsAppGroup chit_id={chit_id} lang={lang} is_admin={is_admin} />

      <div className="cd-tabs">
        <button
          type="button"
          onClick={() => set_tab('overview')}
          className={'cd-tab' + (tab === 'overview' ? ' on' : '')}
        >
          {t('chit_detail.overview', 'Overview')}
        </button>
        <button
          type="button"
          onClick={() => set_tab('ledger')}
          className={'cd-tab' + (tab === 'ledger' ? ' on' : '')}
        >
          {t('chit_detail.ledger_tab', 'Collection Ledger')}
          <span className="cd-tab-badge">{rows.length}×{months.length}</span>
        </button>
        <button
          type="button"
          onClick={() => set_tab('schedule')}
          className={'cd-tab' + (tab === 'schedule' ? ' on' : '')}
        >
          {t('chit_detail.payout_schedule', 'Payout Schedule')}
          <span className="cd-tab-badge">{chit.duration_months}</span>
        </button>
        <button
          type="button"
          onClick={() => set_tab('transactions')}
          className={'cd-tab' + (tab === 'transactions' ? ' on' : '')}
        >
          {t('chit_detail.transactions', 'Transactions')}
        </button>
      </div>

      {tab === 'schedule' && (
        <TimetableTab
          chit_id={chit_id}
          chit={chit}
          memberships={memberships}
          is_admin={is_admin}
          on_open_inline_add={(cycle_index) => set_inline_add({ cycle_index })}
        />
      )}

      {tab === 'overview' && (
        <>
          <OverviewSummary
            plan={plan}
            rows={rows}
            months={months}
            lang={lang}
            on_goto_ledger={() => set_tab('ledger')}
          />
          {is_draft && (
            <div
              className="card"
              style={{ padding: 14, marginBottom: 14, borderColor: all_assigned ? 'var(--good)' : 'var(--warn)' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>
                    {all_assigned
                      ? t('chit_detail.ready', '✓ Ready to publish')
                      : t('chit_detail.draft_assign', 'Draft — assign every instalment before publishing')}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
                    {assigned_count}/{total_cycles} {t('chit_detail.assigned', 'instalments assigned')}
                    {!all_assigned && unassigned_cycles.length > 0
                      ? ` · ${t('chit_detail.missing', 'missing')}: ${unassigned_cycles.map((n) => '#' + n).join(', ')}`
                      : ''}
                  </div>
                </div>
                {!all_assigned && (
                  <button className="btn sm" onClick={() => set_tab('schedule')}>
                    {t('chit_detail.assign_now', 'Assign in Payout Schedule')}
                  </button>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {tab === 'ledger' && (
        <>
          <div style={{ position: 'relative' }}>
          {is_draft && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: 5,
                background: 'rgba(246,242,236,0.55)',
                display: 'grid',
                placeItems: 'center',
                borderRadius: 'var(--radius-lg)',
              }}
            >
              <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 8, padding: '12px 16px', textAlign: 'center', maxWidth: 360, boxShadow: '0 6px 20px rgba(0,0,0,0.10)' }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{t('chit_detail.ledger_locked', 'Collection ledger locked')}</div>
                <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 4 }}>
                  {t('chit_detail.ledger_locked_hint', 'Publish the chit to start collecting payments.')}
                </div>
              </div>
            </div>
          )}
          <div style={{ opacity: is_draft ? 0.5 : 1, pointerEvents: is_draft ? 'none' : 'auto' }}>
          <FullscreenWrap
            is_fullscreen={is_ledger_fs}
            on_exit={() => set_is_ledger_fs(false)}
            label={t('ledger.title', 'Member × Month Collection Ledger')}
          >
          <div id="chit-ledger" className="card" style={{ padding: 0 }}>
          <div className="card-head">
            {/* title + sub grouped so the toggle pin sits at the far right */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="card-title">
                {t('ledger.title', 'Member × Month Collection Ledger')}
              </div>
              <div className="card-sub">
                {rows.length} {t('ledger.subscriber', 'members')} × {months.length}{' '}
                {t('chit_detail.months', 'months')} · {t('ledger.click_hint', 'click any cell for actions')}
              </div>
            </div>
            {/* Draft chits have the whole card behind a lock overlay, so the
                toggle would be unclickable anyway — hide to keep the affordance
                honest. */}
            {!is_draft && (
              <FullscreenToggle
                is_fullscreen={is_ledger_fs}
                on_toggle={() => set_is_ledger_fs((v) => !v)}
              />
            )}
          </div>
          <div className="ledger-wrap">
            <table className="ledger">
              <thead>
                <tr>
                  <th className="col-pos">#</th>
                  <th className="col-sub">{t('ledger.subscriber', 'Member')}</th>
                  {months.map((m, i) => (
                    <th key={m} className="month">
                      <div className="mhead">
                        <span className="mnum">M{i + 1}</span>
                        <span className="mlbl">{ledger_month_label(m)}</span>
                      </div>
                    </th>
                  ))}
                  <th className="col-stats">{t('ledger.collected', 'Collected')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const past = r.cells.filter((c) => c.status !== 'no_row');
                  const expected = past.reduce((s, c) => s + c.expected_amount_paise, 0);
                  const paid = past.reduce((s, c) => s + c.total_paid_paise, 0);
                  const pct = expected === 0 ? 0 : (paid / expected) * 100;
                  return (
                    <tr key={r.slot}>
                      <td className="col-pos">{r.slot}</td>
                      <td className="col-sub">
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, justifyContent: 'space-between' }}>
                          <div>
                            <div className={'led-sub-name ' + (lang === 'ta' ? 'tamil' : '')}>
                              {lang === 'ta' ? r.subscriber?.name_ta : r.subscriber?.name_en}
                            </div>
                            {r.subscriber && <div className="led-sub-meta">{r.subscriber.phone}</div>}
                          </div>
                          {r.subscriber && !is_draft && (
                            <NotifyMenu
                              subscriber_id={r.subscriber.id}
                              intent="due_reminder"
                              context={{ chit_id }}
                            />
                          )}
                        </div>
                      </td>
                      {r.cells.map((c) => {
                        const kind = LEDGER_KIND[c.status] ?? 'upcoming';
                        return (
                          <td
                            key={c.due_month}
                            className={'cell ' + kind}
                            style={{ cursor: 'pointer' }}
                            title={`${c.due_month} · ${c.status}${c.status !== 'no_row' ? ` · ${format_inr(c.total_paid_paise)} / ${format_inr(c.expected_amount_paise)}` : ''}`}
                            onClick={(e) =>
                              set_cell_pop({
                                x: e.clientX,
                                y: e.clientY,
                                slot: r.slot,
                                name: (lang === 'ta' ? r.subscriber?.name_ta : r.subscriber?.name_en) ?? '',
                                phone: r.subscriber?.phone ?? null,
                                subscriber_id: r.subscriber?.id ?? null,
                                due_month: c.due_month,
                                status: c.status,
                                subscription_id: c.subscription_id,
                                expected: c.expected_amount_paise,
                                paid: c.total_paid_paise,
                              })
                            }
                          >
                            <span className="glyph">{LEDGER_GLYPH[kind]}</span>
                          </td>
                        );
                      })}
                      <td className="col-stats">
                        <span className="led-stats-bar">
                          <span
                            className={pct >= 95 ? '' : pct >= 80 ? 'warn' : 'bad'}
                            style={{ width: pct + '%' }}
                          />
                        </span>
                        <span className="amount" style={{ fontSize: 11.5 }}>{pct.toFixed(0)}%</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="ledger-legend">
            <span className="lg"><span className="lg-glyph" style={{ background: 'var(--good)' }} />{t('ledger.paid', 'Fully paid')}</span>
            <span className="lg"><span className="lg-glyph" style={{ background: 'var(--warn)' }} />{t('ledger.partial', 'Partial')}</span>
            <span className="lg"><span className="lg-glyph" style={{ background: 'var(--bad-soft)', border: '1px dashed var(--bad)' }} />{t('ledger.unpaid', 'Overdue')}</span>
            <span className="lg"><span className="lg-glyph" style={{ background: 'var(--surface)', border: '2px solid var(--warn)' }} />{t('ledger.due_now', 'Due now')}</span>
            <span className="lg"><span className="lg-glyph" style={{ background: 'transparent', border: '1px dashed var(--line-strong)' }} />{t('ledger.upcoming', 'Upcoming')}</span>
          </div>
          </div>
          </FullscreenWrap>
          </div>
          </div>
        </>
      )}

      {tab === 'transactions' && (
        <TransactionsTab chit_id={chit_id} lang={lang} />
      )}

      {/* Per-cell popover — opens on any ledger cell (prototype parity). Shows
          cell info + a Mark-paid action for live rows, or a Generate-dues
          action for cells with no row yet (e.g. a future-dated chit). */}
      {cell_pop && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 90 }} onClick={() => set_cell_pop(null)} />
          <div
            className="cell-pop"
            style={{
              position: 'fixed',
              left: Math.max(8, Math.min(cell_pop.x - 140, (typeof window !== 'undefined' ? window.innerWidth : 1280) - 296)),
              top: Math.min(cell_pop.y + 10, (typeof window !== 'undefined' ? window.innerHeight : 800) - 240),
              zIndex: 91,
            }}
          >
            <div className="pop-head">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className={'pop-title ' + (lang === 'ta' ? 'tamil' : '')}>
                  {cell_pop.name || `${t('ledger.slot', 'Slot')} ${cell_pop.slot}`}
                </div>
                <div className="pop-sub">
                  {cell_pop.due_month} · #{cell_pop.slot}{cell_pop.phone ? ` · ${cell_pop.phone}` : ''}
                </div>
              </div>
              <button className="btn ghost sm" onClick={() => set_cell_pop(null)}><Icon.close /></button>
            </div>
            <div className="pop-body">
              <div className="pop-row">
                <span className="l">{t('ledger.status_col', 'Status')}</span>
                <span>{ledger_status_label(cell_pop.status, lang)}</span>
              </div>
              <div className="pop-row">
                <span className="l">{t('ledger.expected', 'Expected')}</span>
                <span className="amount">{format_inr(cell_pop.expected)}</span>
              </div>
              <div className="pop-row">
                <span className="l">{t('ledger.paid', 'Paid')}</span>
                <span className="amount">{format_inr(cell_pop.paid)}</span>
              </div>
            </div>
            <div className="pop-actions">
              {cell_pop.subscriber_id && cell_pop.status !== 'no_row' && (
                <NotifyMenu
                  subscriber_id={cell_pop.subscriber_id}
                  intent={
                    cell_pop.status === 'overdue'
                      ? 'overdue_alert'
                      : cell_pop.status === 'partial'
                        ? 'partial_followup'
                        : cell_pop.status === 'paid'
                          ? 'payment_receipt'
                          : 'due_reminder'
                  }
                  context={{
                    chit_id,
                    ...(cell_pop.subscription_id ? { subscription_id: cell_pop.subscription_id } : {}),
                  }}
                />
              )}
              {cell_pop.subscription_id && cell_pop.status !== 'paid' && (
                <button
                  className="btn sm primary"
                  onClick={() => {
                    set_pay_target({
                      subscription_id: cell_pop.subscription_id!,
                      expected: cell_pop.expected,
                      paid: cell_pop.paid,
                      name: cell_pop.name,
                      ...(cell_pop.subscriber_id ? { subscriber_id: cell_pop.subscriber_id } : {}),
                    });
                    set_cell_pop(null);
                  }}
                >
                  {t('ledger.mark_paid', 'Collect payment')}
                </button>
              )}
              {cell_pop.status === 'paid' && (
                <span style={{ flex: 1, textAlign: 'center', fontSize: 12, color: 'var(--good)' }}>
                  ✓ {t('ledger.paid_full', 'Fully paid')}
                </span>
              )}
              {cell_pop.status === 'no_row' &&
                (is_admin ? (
                  <button
                    className="btn sm primary"
                    disabled={gen_m.isPending}
                    onClick={() => gen_m.mutate(cell_pop.due_month)}
                  >
                    {gen_m.isPending ? '…' : t('ledger.generate_dues', 'Generate dues for this month')}
                  </button>
                ) : (
                  <span style={{ flex: 1, textAlign: 'center', fontSize: 11.5, color: 'var(--ink-3)' }}>
                    {t('ledger.not_generated', 'Dues not generated yet')}
                  </span>
                ))}
            </div>
            {gen_m.error && (
              <div style={{ padding: '0 12px 10px', fontSize: 11, color: 'var(--bad)' }}>
                {friendly_error(gen_m.error)}
              </div>
            )}
          </div>
        </>
      )}

      {pay_target && (
        <MarkPaidModal
          subscription_id={pay_target.subscription_id}
          expected_paise={pay_target.expected}
          total_paid_paise={pay_target.paid}
          subscriber_name={pay_target.name}
          {...(pay_target.subscriber_id ? { subscriber_id: pay_target.subscriber_id } : {})}
          on_close={() => set_pay_target(null)}
        />
      )}

      {show_edit_chit && (
        <ChitEditModal chit={chit} on_close={() => set_show_edit_chit(false)} />
      )}

      {inline_add && (
        <AddSubscriberInlineModal
          chit_id={chit_id}
          cycle_index={inline_add.cycle_index}
          on_close={() => set_inline_add(null)}
        />
      )}
    </div>
  );
}

// ─── Overview tab summary (collection hero + payout snapshot) ───────────────
//
// Prototype-faithful (chitos-prototype chit-detail.jsx ChitOverview). All real
// data: pool/disbursed/next-payout from the cycle plan, the collection figures
// from the ledger matrix. "Disbursed" is derived from elapsed cycles (no
// settlement-tracking backend yet) — correctly 0 for a not-yet-started chit.

function OverviewSummary({
  plan,
  rows,
  months,
  lang,
  on_goto_ledger,
}: {
  plan: CyclePlanRow[];
  rows: MatrixRow[];
  months: string[];
  lang: string;
  on_goto_ledger: () => void;
}): ReactElement {
  const t = (ta: string, en: string): string => (lang === 'ta' ? ta : en);
  const now = new Date();

  const total_pool = plan.reduce((s, p) => s + p.receive_amount_paise, 0);
  const plan_by_idx = new Map(plan.map((p) => [p.cycle_index, p]));
  const disbursed_rows = plan.filter((p) => new Date(p.cycle_date) < now);
  const disbursed = disbursed_rows.reduce((s, p) => s + p.receive_amount_paise, 0);
  const slots = plan.length;
  const next = [...plan]
    .sort((a, b) => a.cycle_index - b.cycle_index)
    .find((p) => new Date(p.cycle_date) >= now) ?? null;
  const next_sub = next ? rows.find((r) => r.slot === next.cycle_index)?.subscriber ?? null : null;
  const next_month = next
    ? new Date(next.cycle_date).toLocaleDateString('en-IN', { month: 'short', year: 'numeric', timeZone: 'UTC' })
    : '—';

  // Per-month collection aggregates from the matrix.
  const month_agg = months.map((mo, mi) => {
    let expected = 0;
    let paid = 0;
    let c_paid = 0;
    let c_overdue = 0;
    for (const r of rows) {
      const cell = r.cells[mi];
      if (!cell) continue;
      expected += cell.expected_amount_paise;
      paid += cell.total_paid_paise;
      if (cell.status === 'paid') c_paid += 1;
      else if (cell.status === 'overdue') c_overdue += 1;
    }
    return { mo, mi, expected, paid, c_paid, c_overdue, total: rows.length };
  });
  // Active instalment = first month not fully collected, else the last month.
  const active = month_agg.find((m) => m.paid < m.expected) ?? month_agg[month_agg.length - 1] ?? null;
  const pct = active && active.expected > 0 ? (active.paid / active.expected) * 100 : 0;
  const active_cycle = active ? plan_by_idx.get(active.mi + 1) : undefined;
  const due_date = active_cycle ? format_date_dmy(active_cycle.cycle_date) : active?.mo ?? '';

  return (
    <>
      {/* Collection hero — this instalment's progress */}
      <div
        className="card"
        style={{
          marginBottom: 14,
          background: 'linear-gradient(180deg, var(--primary-soft), var(--surface))',
          borderColor: 'var(--primary)',
        }}
      >
        <div style={{ padding: '16px 18px 14px', display: 'grid', gridTemplateColumns: '1fr auto', gap: 18, alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--primary-ink)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
              {t('கட்டம்', 'Instalment')} {active ? active.mi + 1 : 1} {t('வசூல்', 'collection')} — {t('கடைசி', 'due')} {due_date}
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 4, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 28, fontWeight: 600, fontFamily: 'var(--font-mono)', color: 'var(--primary-ink)' }}>
                {format_inr(active?.paid ?? 0)}
              </div>
              <div className="mono" style={{ fontSize: 14, color: 'var(--ink-3)' }}>/ {format_inr(active?.expected ?? 0)}</div>
              <div style={{ fontSize: 12, color: 'var(--ink-2)' }}>
                · {active?.c_paid ?? 0} {t('செலுத்தினர்', 'paid')} · {active ? active.total - active.c_paid : 0} {t('நிலுவை', 'pending')}
                {active && active.c_overdue > 0 && (
                  <span style={{ color: 'var(--bad)' }}> · {active.c_overdue} {t('தாமதம்', 'overdue')}</span>
                )}
              </div>
            </div>
            <div style={{ height: 8, background: 'rgba(0,0,0,0.08)', borderRadius: 4, marginTop: 10, overflow: 'hidden', maxWidth: 480 }}>
              <div style={{ width: pct + '%', height: '100%', background: 'var(--primary)' }} />
            </div>
            <div className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)', marginTop: 5 }}>
              {pct.toFixed(0)}% {t('முடிந்தது', 'collected')}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 180 }}>
            <button className="btn primary" onClick={on_goto_ledger}>{t('செலுத்தல் பதிவு', 'Mark payment')}</button>
          </div>
        </div>
      </div>

      {/* Payout snapshot strip */}
      <div className="cd-payout-strip">
        <div className="cd-pkpi">
          <div className="cd-pkpi-lbl">{t('மொத்த வழங்கல்', 'Total payout pool')}</div>
          <div className="cd-pkpi-val mono">{format_inr(total_pool)}</div>
          <div className="cd-pkpi-sub">{slots} {t('இடங்கள்', 'slots')}</div>
        </div>
        <div className="cd-pkpi">
          <div className="cd-pkpi-lbl">{t('வழங்கப்பட்டது', 'Disbursed')}</div>
          <div className="cd-pkpi-val mono">{format_inr(disbursed)}</div>
          <div className="cd-pkpi-sub">{disbursed_rows.length} {t('இடங்கள்', 'slots')}</div>
        </div>
        <div className="cd-pkpi">
          <div className="cd-pkpi-lbl">{t('மீதம்', 'Remaining')}</div>
          <div className="cd-pkpi-val mono">{format_inr(total_pool - disbursed)}</div>
          <div className="cd-pkpi-sub">{slots - disbursed_rows.length} {t('இடங்கள்', 'slots')}</div>
        </div>
        <div className="cd-pkpi cd-pkpi-next">
          <div className="cd-pkpi-lbl">{t('அடுத்த வழங்கல்', 'Next payout')}</div>
          <div className="cd-pkpi-val">
            {next && <span className="cd-slotchip mono">#{next.cycle_index}</span>}
            <span className={'cd-pkpi-name ' + (lang === 'ta' ? 'tamil' : '')}>
              {next_sub ? (lang === 'ta' ? next_sub.name_ta : next_sub.name_en) : t('ஒதுக்கப்படவில்லை', 'Unassigned')}
            </span>
          </div>
          <div className="cd-pkpi-sub mono">{next ? `${next_month} · ${format_inr(next.receive_amount_paise)}` : '—'}</div>
        </div>
      </div>
    </>
  );
}

// ─── Timetable tab ────────────────────────────────────────────────────────
//
// Renders the canonical Tamil chit-fund schedule: one row per cycle, with
// editable pay/receive amounts and a subscriber dropdown for each cycle.
// Every cycle is uniform — any cycle (including #1) takes any subscriber;
// there is no special foreman cycle. Cycles where any payment has been
// recorded are marked locked (backend rejects edits regardless).

function TimetableTab({
  chit_id,
  chit,
  memberships,
  is_admin,
  on_open_inline_add,
}: {
  chit_id: string;
  chit: Chit;
  memberships: MembershipRow[];
  is_admin: boolean;
  on_open_inline_add: (cycle_index: number) => void;
}): ReactElement {
  const qc = useQueryClient();
  const lang = current_language();
  // Local to the schedule card so it can be expanded without affecting the
  // ledger's own fullscreen state. Two sections, two independent overlays.
  const [is_fs, set_is_fs] = useState(false);
  const [show_import, set_show_import] = useState(false);

  const plan_q = useQuery({
    queryKey: ['cycle-plan', chit_id],
    queryFn: () => orpc.chits.get_cycle_plan({ chit_id }),
    enabled: Boolean(chit_id),
  });

  const update_m = useMutation({
    mutationFn: (args: {
      cycle_index: number;
      pay_amount_paise?: number;
      receive_amount_paise?: number;
      recipient_subscriber_id?: string | null;
    }) => orpc.chits.update_cycle_plan_row({ chit_id, ...args }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cycle-plan', chit_id] });
      void qc.invalidateQueries({ queryKey: ['ledger-matrix', chit_id] });
    },
  });

  // Excel-style paste handler — invoked from CycleRow's onPaste when the
  // clipboard text contains a tab or newline (single-value paste falls through
  // to the browser's default).
  //
  // Grid mapping:
  //   - Lines (split on \n, trimmed of \r) become consecutive rows starting at
  //     `start_cycle_index`. Stops at the last row of the plan.
  //   - Each line's columns (split on \t) project from `start_col` outward:
  //     starting on Pay → [pay, receive]; starting on Receive → [receive only]
  //     (no wrap into the next row's Pay — Excel doesn't do that either).
  //   - Tokens have ₹, commas, and whitespace stripped, then Number-parsed.
  //     Empty cells and NaN are skipped (preserves the existing value).
  //
  // Each row fires its own update_cycle_plan_row mutation in parallel; the
  // backend handles them independently and the one cycle-plan refetch at the
  // end re-syncs the UI.
  function paste_grid(
    start_cycle_index: number,
    start_col: 'pay' | 'receive',
    text: string,
    rows_now: CyclePlanRow[],
  ): void {
    const lines = text.replace(/\r/g, '').split('\n').filter((l) => l !== '');
    if (lines.length === 0) return;
    const cols: Array<'pay' | 'receive'> = start_col === 'pay' ? ['pay', 'receive'] : ['receive'];

    let target_cycle = start_cycle_index;
    for (const line of lines) {
      const target = rows_now.find((r) => r.cycle_index === target_cycle);
      if (!target) break; // walked past the end of the plan
      const tokens = line.split('\t');
      const args: { pay_amount_paise?: number; receive_amount_paise?: number } = {};
      for (let c = 0; c < tokens.length && c < cols.length; c++) {
        // noUncheckedIndexedAccess makes these `string | undefined`; bind to
        // locals so the narrowing carries through to the writes below.
        const tok = tokens[c];
        const col = cols[c];
        if (tok === undefined || col === undefined) continue;
        const raw = tok.trim().replace(/[,₹\s]/g, '');
        if (raw === '') continue;
        const n = Number(raw);
        if (!Number.isFinite(n) || n < 0) continue;
        const paise = Math.round(n * 100);
        if (col === 'pay') args.pay_amount_paise = paise;
        else args.receive_amount_paise = paise;
      }
      if (Object.keys(args).length > 0) {
        update_m.mutate({ cycle_index: target_cycle, ...args });
      }
      target_cycle += 1;
    }
  }

  const ramp_m = useMutation({
    mutationFn: () => orpc.chits.apply_linear_ramp({ chit_id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cycle-plan', chit_id] }),
  });

  if (plan_q.isLoading) return <div className="text-sm text-ink/60">Loading schedule…</div>;
  if (plan_q.error) {
    return <div className="text-sm text-bad">{friendly_error(plan_q.error)}</div>;
  }
  if (!plan_q.data) return <></>;

  const rows = plan_q.data.rows as CyclePlanRow[];
  // Subscribers eligible to be assigned: anyone who is or has been a member
  // of this chit (active OR substituted). Substituted-only entries are
  // marked so the dropdown can show "(out)" — they can still be picked
  // for an empty cycle, and the backend handles reactivating them.
  // Already-assigned subscribers are filtered out of OTHER cycles' dropdowns.
  const assigned_by_subscriber = new Map<string, number>();
  for (const r of rows) {
    if (r.recipient_subscriber_id) assigned_by_subscriber.set(r.recipient_subscriber_id, r.cycle_index);
  }
  const eligible_subscribers = memberships
    .filter((m): m is MembershipRow & { subscriber: Subscriber } => !!m.subscriber)
    .map((m) => m.subscriber);

  const assigned_total = rows.length;
  const assigned_done = rows.filter((r) => r.recipient_subscriber_id).length;
  const all_assigned = assigned_total > 0 && assigned_done === assigned_total;

  return (
    <>
    <FullscreenWrap
      is_fullscreen={is_fs}
      on_exit={() => set_is_fs(false)}
      label={lang === 'ta' ? 'பணம் செலுத்த/பெற அட்டவணை' : 'Per-cycle pay/receive schedule'}
    >
    <div className="card" style={{ padding: 0 }}>
      <div className="card-head">
        <div className="card-sub" style={{ flex: 1 }}>
          {lang === 'ta'
            ? 'ஒவ்வொரு கட்டத்திற்கும் கட்டணம்/பெறும் தொகை. எந்தக் கட்டத்திற்கும் எந்தச் சந்தாதாரரையும் ஒதுக்கலாம். செலுத்திய கட்டங்கள் பூட்டப்பட்டவை.'
            : 'Per-cycle pay/receive schedule. Assign any subscriber to any cycle. Paid cycles are locked.'}
        </div>
        <span
          className="mono"
          title={all_assigned ? undefined : `${assigned_total - assigned_done} unassigned`}
          style={{
            fontSize: 11.5,
            fontWeight: 600,
            padding: '3px 9px',
            borderRadius: 999,
            whiteSpace: 'nowrap',
            background: all_assigned ? 'var(--good-soft)' : 'var(--warn-soft)',
            color: all_assigned ? 'var(--good)' : 'var(--warn)',
          }}
        >
          {assigned_done}/{assigned_total} {lang === 'ta' ? 'ஒதுக்கப்பட்டது' : 'assigned'}
        </span>
        {is_admin && (
          <div className="card-actions">
            <button
              className="btn sm"
              onClick={() => set_show_import(true)}
              title={lang === 'ta' ? 'காகித லெட்ஜர் புகைப்படத்திலிருந்து அட்டவணையை இறக்குமதி செய்' : 'Import the schedule from a photo of the paper ledger'}
            >
              📷 {lang === 'ta' ? 'புகைப்படத்திலிருந்து இறக்குமதி' : 'Import from photo'}
            </button>
            <button
              className="btn sm"
              onClick={() => ramp_m.mutate()}
              disabled={ramp_m.isPending}
              title="Reset all unlocked cycles to the default amounts (pay = monthly base, receive = pot - 2%)"
            >
              {ramp_m.isPending ? (lang === 'ta' ? 'மீட்டமைக்கிறது…' : 'Resetting…') : (lang === 'ta' ? 'இயல்பு தொகைகளுக்கு மீட்டமை' : 'Reset amounts to default')}
            </button>
          </div>
        )}
        <FullscreenToggle is_fullscreen={is_fs} on_toggle={() => set_is_fs((v) => !v)} />
      </div>

      {/* dues-table-wrap class lets the fullscreen overlay CSS know to flex
          this scroll area to the remaining viewport instead of letting the
          inline default (auto height) cap it. */}
      <div className="dues-table-wrap" style={{ overflowX: 'auto' }}>
        <table className="dues-table">
          <thead>
            <tr>
              <th style={{ width: 56 }}>#</th>
              <th style={{ width: 128 }}>{lang === 'ta' ? 'தேதி' : 'Date'}</th>
              <th style={{ textAlign: 'right', width: 128 }}>{lang === 'ta' ? 'கட்டணம் (₹)' : 'Pay (₹)'}</th>
              <th style={{ textAlign: 'right', width: 128 }}>{lang === 'ta' ? 'பெறும் (₹)' : 'Receive (₹)'}</th>
              <th>{lang === 'ta' ? 'பெறுநர்' : 'Assigned to'}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <CycleRow
                key={r.id}
                row={r}
                pool_size={chit.pool_size}
                eligible_subscribers={eligible_subscribers}
                assigned_by_subscriber={assigned_by_subscriber}
                lang={lang}
                on_update={(args) => update_m.mutate({ cycle_index: r.cycle_index, ...args })}
                on_add_new={() => on_open_inline_add(r.cycle_index)}
                on_paste_grid={(start_col, text) => paste_grid(r.cycle_index, start_col, text, rows)}
                /* Per-row inputs only disable when the user lacks admin scope.
                   The previous flag (`update_m.isPending`) made every row in
                   the table go disabled the instant any single Pay/Receive
                   commit fired — which made Tab skip the next input and felt
                   like the table was locking on blur. */
                disabled={!is_admin}
                /* Tells CycleRow whether THIS row's last write is in flight,
                   purely for a small "saving…" affordance on the row itself. */
                row_saving={update_m.isPending && update_m.variables?.cycle_index === r.cycle_index}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Discoverability strip — the keyboard + paste affordances aren't
          visible otherwise. Stays subtle and out of the way. */}
      <div
        style={{
          padding: '8px 14px',
          borderTop: '1px solid var(--line)',
          fontSize: 11,
          color: 'var(--ink-3)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 14,
        }}
      >
        <span>⌨ <b>Tab</b> {lang === 'ta' ? 'அடுத்த கட்டம்' : 'next field'}</span>
        <span><b>Enter</b> {lang === 'ta' ? 'அடுத்த வரிசை' : 'next row'} · <b>Shift+Enter</b> {lang === 'ta' ? 'முந்தைய' : 'previous'}</span>
        <span><b>Esc</b> {lang === 'ta' ? 'மீட்டமை' : 'revert'}</span>
        <span>📋 {lang === 'ta' ? 'Excel-லிருந்து நகலெடுத்து ஒட்டலாம் — பல வரிசைகள்/நெடுவரிசைகள் ஒரே நேரத்தில்' : 'Paste from Excel — multi-row & multi-column supported'}</span>
      </div>

      {update_m.error && (
        <div className="mt-3 text-xs text-bad bg-bad-soft px-3 py-2 rounded">
          {friendly_error(update_m.error)}
        </div>
      )}
    </div>
    </FullscreenWrap>
    {show_import && (
      <ImportScheduleModal
        chit_id={chit_id}
        chit={chit}
        lang={lang}
        on_close={() => set_show_import(false)}
      />
    )}
    </>
  );
}

function CycleRow({
  row,
  pool_size,
  eligible_subscribers,
  assigned_by_subscriber,
  lang,
  on_update,
  on_add_new,
  on_paste_grid,
  disabled,
  row_saving,
}: {
  row: CyclePlanRow;
  pool_size: number;
  eligible_subscribers: Subscriber[];
  assigned_by_subscriber: Map<string, number>;
  lang: string;
  on_update: (args: {
    pay_amount_paise?: number;
    receive_amount_paise?: number;
    recipient_subscriber_id?: string | null;
  }) => void;
  on_add_new: () => void;
  on_paste_grid: (start_col: 'pay' | 'receive', text: string) => void;
  disabled: boolean;
  row_saving: boolean;
}): ReactElement {
  const [pay, set_pay] = useState((row.pay_amount_paise / 100).toString());
  const [receive, set_receive] = useState((row.receive_amount_paise / 100).toString());
  // Refs for selector-based focus jumps (Enter / Shift+Enter) and for guarding
  // useEffect resync against active typing. Also caches the last value we
  // committed so we don't double-fire identical mutations between local
  // change → blur (which fires the keydown commit too).
  const pay_ref = useRef<HTMLInputElement>(null);
  const receive_ref = useRef<HTMLInputElement>(null);
  const last_pay_paise = useRef(row.pay_amount_paise);
  const last_receive_paise = useRef(row.receive_amount_paise);

  // Sync local edit state when the server's value changes (refetch after
  // mutation, parallel paste landing here, edit by another tab). Skip when
  // the user is actively typing in this cell so we don't yank their input.
  useEffect(() => {
    last_pay_paise.current = row.pay_amount_paise;
    if (document.activeElement !== pay_ref.current) {
      set_pay((row.pay_amount_paise / 100).toString());
    }
  }, [row.pay_amount_paise]);
  useEffect(() => {
    last_receive_paise.current = row.receive_amount_paise;
    if (document.activeElement !== receive_ref.current) {
      set_receive((row.receive_amount_paise / 100).toString());
    }
  }, [row.receive_amount_paise]);

  function commit(col: 'pay' | 'receive'): void {
    const raw = col === 'pay' ? pay : receive;
    const v = Math.round(Number(raw) * 100);
    const last = col === 'pay' ? last_pay_paise.current : last_receive_paise.current;
    if (!Number.isFinite(v) || v < 0 || v === last) {
      // Revert local edit to the last-known-good value. Covers: blank input,
      // typos like "5o00", and no-op blurs (user just clicked through).
      const restore = (last / 100).toString();
      if (col === 'pay') set_pay(restore);
      else set_receive(restore);
      return;
    }
    // Cache before dispatch so an immediate second commit (blur after Enter)
    // is a no-op instead of a duplicate mutation.
    if (col === 'pay') last_pay_paise.current = v;
    else last_receive_paise.current = v;
    on_update(col === 'pay' ? { pay_amount_paise: v } : { receive_amount_paise: v });
  }

  // Jump focus to the same column in another cycle by data-attribute lookup.
  // Walks past disabled targets so paid/locked cycles don't trap the cursor.
  function focus_cycle(target_cycle_index: number, col: 'pay' | 'receive', step: 1 | -1): void {
    let target = target_cycle_index;
    // Bounded scan — if everything down/up is disabled we just stop.
    for (let i = 0; i < 200; i++) {
      const el = document.querySelector<HTMLInputElement>(
        `input[data-cycle="${target}"][data-col="${col}"]`,
      );
      if (!el) return;
      if (!el.disabled) {
        el.focus();
        el.select();
        return;
      }
      target += step;
    }
  }

  function on_key_down(col: 'pay' | 'receive') {
    return (e: React.KeyboardEvent<HTMLInputElement>): void => {
      if (e.key === 'Enter') {
        // Excel pattern: Enter commits and moves down a row in the same column;
        // Shift+Enter moves up. Tab/Shift+Tab handled natively across columns.
        e.preventDefault();
        commit(col);
        focus_cycle(row.cycle_index + (e.shiftKey ? -1 : 1), col, e.shiftKey ? -1 : 1);
      } else if (e.key === 'Escape') {
        // Discard the edit and blur — restores last-committed value via the
        // sync useEffect.
        e.preventDefault();
        const last = col === 'pay' ? last_pay_paise.current : last_receive_paise.current;
        if (col === 'pay') set_pay((last / 100).toString());
        else set_receive((last / 100).toString());
        e.currentTarget.blur();
      }
      // Tab: native browser handling. With `disabled` no longer flipping
      // mid-stream, focus moves Pay → Receive → recipient → next row's Pay.
    };
  }

  function on_paste(col: 'pay' | 'receive') {
    return (e: React.ClipboardEvent<HTMLInputElement>): void => {
      const text = e.clipboardData.getData('text/plain');
      // Single-value paste (no tab, no newline) → let the browser handle it.
      // Multi-cell paste → take over, distribute across rows.
      if (!text || (!text.includes('\n') && !text.includes('\t'))) return;
      e.preventDefault();
      on_paste_grid(col, text);
    };
  }

  const recipient = eligible_subscribers.find((s) => s.id === row.recipient_subscriber_id) ?? null;

  return (
    <tr
      className="border-b border-line/40 last:border-b-0"
      style={row_saving ? { background: 'var(--bg-elev)' } : undefined}
      title={row_saving ? 'Saving…' : undefined}
    >
      <td className="px-3 py-2 font-mono text-ink/70">{row.cycle_index}</td>
      <td className="px-3 py-2 text-ink/70 font-mono text-xs">{format_date_dmy(row.cycle_date)}</td>
      <td className="px-3 py-2 text-right">
        <input
          ref={pay_ref}
          type="number"
          inputMode="decimal"
          min={0}
          step="any"
          value={pay}
          data-cycle={row.cycle_index}
          data-col="pay"
          onChange={(e) => set_pay(e.target.value)}
          onBlur={() => commit('pay')}
          onKeyDown={on_key_down('pay')}
          onPaste={on_paste('pay')}
          onFocus={(e) => e.currentTarget.select()}
          disabled={disabled}
          className="w-28 px-2 py-1 text-right bg-surface border border-line rounded font-mono text-sm disabled:bg-bg-elev disabled:text-ink/40 focus:border-primary focus:outline-none"
        />
      </td>
      <td className="px-3 py-2 text-right">
        <input
          ref={receive_ref}
          type="number"
          inputMode="decimal"
          min={0}
          step="any"
          value={receive}
          data-cycle={row.cycle_index}
          data-col="receive"
          onChange={(e) => set_receive(e.target.value)}
          onBlur={() => commit('receive')}
          onKeyDown={on_key_down('receive')}
          onPaste={on_paste('receive')}
          onFocus={(e) => e.currentTarget.select()}
          disabled={disabled}
          className="w-28 px-2 py-1 text-right bg-surface border border-line rounded font-mono text-sm disabled:bg-bg-elev disabled:text-ink/40 focus:border-primary focus:outline-none"
        />
      </td>
      <td className="px-3 py-2">
        {/* Every cycle (including #1) is a plain recipient slot. Assign any
            subscriber; the company/foreman is just a subscriber if needed. */}
        <div className="flex items-center gap-2">
          <select
            value={row.recipient_subscriber_id ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              if (v === '__add_new__') {
                on_add_new();
                return;
              }
              on_update({ recipient_subscriber_id: v === '' ? null : v });
            }}
            disabled={disabled}
            className="flex-1 max-w-xs px-2 py-1 bg-surface border border-line rounded text-sm disabled:bg-bg-elev disabled:text-ink/40"
          >
            <option value="">— unassigned —</option>
            <option value="__add_new__">
              {eligible_subscribers.length === 0
                ? '+ Pick a member… (any chit or new)'
                : '+ Pick another member… (any chit or new)'}
            </option>
            {eligible_subscribers.length > 0 && (
              <option disabled>──── this chit ────</option>
            )}
            {eligible_subscribers.map((s) => {
              const display = lang === 'ta' ? s.name_ta : s.name_en;
              const used_at = assigned_by_subscriber.get(s.id);
              const used_elsewhere = used_at !== undefined && used_at !== row.cycle_index;
              if (used_elsewhere) return null;
              return (
                <option key={s.id} value={s.id}>
                  {display}
                </option>
              );
            })}
          </select>
        </div>
        {recipient && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
            <span className="text-[10px] text-ink/40 font-mono">{recipient.phone}</span>
            <NotifyMenu
              subscriber_id={recipient.id}
              intent="payout_scheduled"
              context={{ chit_id: row.chit_id, payout_cycle_index: row.cycle_index }}
            />
          </div>
        )}
      </td>
    </tr>
  );
  // Note: pool_size used implicitly through eligible_subscribers list size.
  void pool_size;
}

// ─── TransactionsTab ──────────────────────────────────────────────────────
//
// Flat audit log of every payment recorded against this chit. Reads from
// orpc.chits.list_transactions which unwinds `subscription_ledger.payments[]`
// server-side and joins the payer (subscriber name + phone). Newest-first.
// Includes a search box (name/phone/ref) and a method filter so the operator
// can scan for "all UPI today" or find a specific reference fast.

interface TransactionRow {
  payment_id: string;
  subscription_id: string;
  subscriber_id: string;
  subscriber_name_en: string | null;
  subscriber_name_ta: string | null;
  subscriber_phone: string | null;
  amount_paise: number;
  method: string;
  reference: string | null;
  timestamp: string | Date;
  due_month: string;
  note: string | null;
  screenshot_key: string | null;
  collector_id: string | null;
}

function TransactionsTab({
  chit_id,
  lang,
}: {
  chit_id: string;
  lang: string;
}): ReactElement {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ['chit-transactions', chit_id],
    queryFn: () => orpc.chits.list_transactions({ chit_id }),
    enabled: !!chit_id,
  });
  const [method_filter, set_method_filter] = useState<'all' | string>('all');
  const [search, set_search] = useState('');

  if (q.isLoading) return <div className="text-sm text-ink/60">Loading transactions…</div>;
  if (q.error) return <div className="text-sm text-bad">{friendly_error(q.error)}</div>;
  const all = ((q.data?.payments ?? []) as TransactionRow[]).slice();

  // Distinct methods present in the data drive the chip rail — no point
  // showing a "Cheque" chip if no cheque payments exist.
  const methods = Array.from(new Set(all.map((p) => p.method))).sort();
  const total = all.reduce((s, p) => s + p.amount_paise, 0);
  const by_method = methods.map((m) => ({
    method: m,
    count: all.filter((p) => p.method === m).length,
    total: all.filter((p) => p.method === m).reduce((s, p) => s + p.amount_paise, 0),
  }));

  const lc = search.trim().toLowerCase();
  const shown = all.filter((p) => {
    if (method_filter !== 'all' && p.method !== method_filter) return false;
    if (!lc) return true;
    return (
      (p.subscriber_name_en ?? '').toLowerCase().includes(lc) ||
      (p.subscriber_name_ta ?? '').includes(search.trim()) ||
      (p.subscriber_phone ?? '').includes(lc) ||
      (p.reference ?? '').toLowerCase().includes(lc)
    );
  });

  return (
    <div className="card" style={{ padding: 0 }}>
      <div className="card-head">
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="card-title">{t('chit_detail.transactions', 'Transactions')}</div>
          <div className="card-sub">
            {all.length} {t('chit_detail.payments', 'payments')} ·{' '}
            {t('chit_detail.total', 'total')} {format_inr(total)}
          </div>
        </div>
      </div>

      <div
        className="filter-rail"
        style={{ padding: '10px 14px', borderBottom: '1px solid var(--line)', display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}
      >
        <button
          className={'chip' + (method_filter === 'all' ? ' on' : '')}
          onClick={() => set_method_filter('all')}
        >
          {t('chit_detail.all', 'All')} <span className="ct mono">{all.length}</span>
        </button>
        {by_method.map(({ method, count }) => (
          <button
            key={method}
            className={'chip' + (method_filter === method ? ' on' : '')}
            onClick={() => set_method_filter(method)}
          >
            {method.toUpperCase()} <span className="ct mono">{count}</span>
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <input
          type="text"
          placeholder={t('chit_detail.search_tx', 'Search name, phone, reference…') as string}
          value={search}
          onChange={(e) => set_search(e.target.value)}
          style={{
            padding: '6px 10px',
            border: '1px solid var(--line)',
            borderRadius: 6,
            background: 'var(--surface)',
            fontSize: 12,
            minWidth: 220,
          }}
        />
      </div>

      <div className="dues-table-wrap" style={{ overflowX: 'auto' }}>
        <table className="dues-table">
          <thead>
            <tr>
              <th style={{ width: 130 }}>{t('chit_detail.when', 'When')}</th>
              <th>{t('chit_detail.subscriber', 'Member')}</th>
              <th style={{ width: 100 }}>{t('chit_detail.for_month', 'For month')}</th>
              <th style={{ width: 110 }}>{t('chit_detail.method', 'Method')}</th>
              <th>{t('chit_detail.reference', 'Reference')}</th>
              <th style={{ textAlign: 'right', width: 110 }}>{t('chit_detail.amount', 'Amount')}</th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: 18, color: 'var(--ink-3)' }}>
                  {all.length === 0
                    ? t('chit_detail.no_transactions', 'No payments recorded yet')
                    : t('chit_detail.no_matches', 'No matches — adjust filters')}
                </td>
              </tr>
            )}
            {shown.map((p) => {
              const when = new Date(p.timestamp);
              const name = lang === 'ta' ? p.subscriber_name_ta : p.subscriber_name_en;
              return (
                <tr key={p.payment_id} className="border-b border-line/40 last:border-b-0">
                  <td className="px-3 py-2 font-mono text-xs">
                    <div>{when.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                    <div style={{ color: 'var(--ink-3)' }}>
                      {when.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className={lang === 'ta' ? 'tamil' : ''}>{name ?? '—'}</div>
                    {p.subscriber_phone && (
                      <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{p.subscriber_phone}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{p.due_month}</td>
                  <td className="px-3 py-2">
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: 999,
                        background: 'var(--bg-elev)',
                        color: 'var(--ink)',
                        textTransform: 'uppercase',
                      }}
                    >
                      {p.method}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs" style={{ color: 'var(--ink-3)' }}>
                    {p.reference ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-right font-mono" style={{ fontWeight: 600 }}>
                    {format_inr(p.amount_paise)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── ChitEditModal ────────────────────────────────────────────────────────

function ChitEditModal({
  chit,
  on_close,
}: {
  chit: Chit;
  on_close: () => void;
}): ReactElement {
  const qc = useQueryClient();
  const is_draft = chit.status === 'draft';
  const start_date_iso =
    typeof chit.start_date === 'string'
      ? chit.start_date.slice(0, 10)
      : new Date(chit.start_date).toISOString().slice(0, 10);

  const [name_ta, set_name_ta] = useState(chit.name_ta);
  const [name_en, set_name_en] = useState(chit.name_en);
  const [pool_size, set_pool_size] = useState(chit.pool_size.toString());
  const [duration_months, set_duration_months] = useState(chit.duration_months.toString());
  const [start_date, set_start_date] = useState(start_date_iso);
  const [monthly_rupees, set_monthly_rupees] = useState((chit.monthly_subscription_paise / 100).toString());
  const [commission_pct, set_commission_pct] = useState(chit.foreman_commission_pct.toString());
  const [collection_day, set_collection_day] = useState(chit.collection_day.toString());
  const [status, set_status] = useState(chit.status as 'draft' | 'active' | 'completed' | 'cancelled');

  type ChitStatus = 'draft' | 'active' | 'completed' | 'cancelled';
  type UpdateArgs = {
    name_ta?: string;
    name_en?: string;
    pool_size?: number;
    duration_months?: number;
    start_date?: string;
    monthly_subscription_paise?: number;
    foreman_commission_pct?: number;
    collection_day?: number;
    status?: ChitStatus;
  };

  const save = useMutation({
    mutationFn: (args: UpdateArgs) => orpc.chits.update({ chit_id: chit.id, ...args }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ledger-matrix', chit.id] });
      void qc.invalidateQueries({ queryKey: ['cycle-plan', chit.id] });
      void qc.invalidateQueries({ queryKey: ['memberships', chit.id] });
      on_close();
    },
  });

  function submit(e: React.FormEvent): void {
    e.preventDefault();
    const args: UpdateArgs = {};
    if (name_ta !== chit.name_ta) args.name_ta = name_ta;
    if (name_en !== chit.name_en) args.name_en = name_en;
    const monthly_paise = Math.round(Number(monthly_rupees) * 100);
    if (Number.isFinite(monthly_paise) && monthly_paise > 0 && monthly_paise !== chit.monthly_subscription_paise) {
      args.monthly_subscription_paise = monthly_paise;
    }
    const commission = Number(commission_pct);
    if (Number.isFinite(commission) && commission !== chit.foreman_commission_pct) {
      args.foreman_commission_pct = commission;
    }
    const day = Number(collection_day);
    if (Number.isFinite(day) && day !== chit.collection_day) {
      args.collection_day = day;
    }
    if (status !== chit.status) args.status = status;

    // Shape fields are only sent if we're in draft (backend enforces this
    // too; we just save a round-trip).
    if (is_draft) {
      const ps = Number(pool_size);
      if (Number.isFinite(ps) && ps !== chit.pool_size) args.pool_size = ps;
      const dm = Number(duration_months);
      if (Number.isFinite(dm) && dm !== chit.duration_months) args.duration_months = dm;
      if (start_date !== start_date_iso) args.start_date = start_date;
    }

    save.mutate(args);
  }

  const wants_shape_change =
    is_draft && (
      Number(pool_size) !== chit.pool_size ||
      Number(duration_months) !== chit.duration_months ||
      start_date !== start_date_iso
    );

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={on_close}>
      <div
        className="bg-surface border border-line rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-medium mb-4">
          Edit chit
          {is_draft && (
            <span className="ml-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-warn-soft text-warn align-middle">
              draft
            </span>
          )}
        </h2>
        {is_draft && wants_shape_change && (
          <div className="mb-4 text-xs text-warn bg-warn-soft px-3 py-2 rounded">
            Changing pool size, duration, or start date will reset cycle amounts and clear any subscribers at removed slots.
          </div>
        )}
        <form onSubmit={submit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-wider text-ink/50 mb-1">
                Name (Tamil)
              </label>
              <input
                type="text"
                value={name_ta}
                onChange={(e) => set_name_ta(e.target.value)}
                required
                minLength={1}
                maxLength={120}
                className="w-full px-3 py-2 bg-surface border border-line rounded text-sm font-tamil"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-ink/50 mb-1">
                Name (English)
              </label>
              <input
                type="text"
                value={name_en}
                onChange={(e) => set_name_en(e.target.value)}
                required
                minLength={1}
                maxLength={120}
                className="w-full px-3 py-2 bg-surface border border-line rounded text-sm"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-ink/50 mb-1">
                Monthly subscription (₹)
              </label>
              <input
                type="number"
                inputMode="numeric"
                min={1}
                value={monthly_rupees}
                onChange={(e) => set_monthly_rupees(e.target.value)}
                required
                className="w-full px-3 py-2 bg-surface border border-line rounded text-sm font-mono"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-ink/50 mb-1">
                Foreman commission (%)
              </label>
              <input
                type="number"
                step="0.5"
                min={0}
                max={20}
                value={commission_pct}
                onChange={(e) => set_commission_pct(e.target.value)}
                required
                className="w-full px-3 py-2 bg-surface border border-line rounded text-sm font-mono"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-ink/50 mb-1">
                Collection day (1–28)
              </label>
              <input
                type="number"
                min={1}
                max={28}
                value={collection_day}
                onChange={(e) => set_collection_day(e.target.value)}
                required
                className="w-full px-3 py-2 bg-surface border border-line rounded text-sm font-mono"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-ink/50 mb-1">
                Status
              </label>
              <select
                value={status}
                onChange={(e) => set_status(e.target.value as 'draft' | 'active' | 'completed' | 'cancelled')}
                className="w-full px-3 py-2 bg-surface border border-line rounded text-sm"
              >
                {is_draft && <option value="draft">Draft</option>}
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          <div className="mt-6 border-t border-line pt-4">
            <p className="text-[11px] text-ink/50 mb-2">
              {is_draft
                ? 'Shape — editable while the chit is in draft. Locks after Publish.'
                : 'Locked — these fields shape the cycle plan and cannot change after Publish.'}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-wider text-ink/40 mb-1">Pool size</label>
                <input
                  type="number"
                  min={2}
                  max={20}
                  value={is_draft ? pool_size : chit.pool_size}
                  onChange={(e) => set_pool_size(e.target.value)}
                  disabled={!is_draft}
                  className={`w-full px-3 py-2 border border-line rounded text-sm font-mono ${
                    is_draft ? 'bg-surface' : 'bg-bg-elev text-ink/50'
                  }`}
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-ink/40 mb-1">Duration (months)</label>
                <input
                  type="number"
                  min={2}
                  max={60}
                  value={is_draft ? duration_months : chit.duration_months}
                  onChange={(e) => set_duration_months(e.target.value)}
                  disabled={!is_draft}
                  className={`w-full px-3 py-2 border border-line rounded text-sm font-mono ${
                    is_draft ? 'bg-surface' : 'bg-bg-elev text-ink/50'
                  }`}
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-ink/40 mb-1">Start date</label>
                <input
                  type="date"
                  value={is_draft ? start_date : start_date_iso}
                  onChange={(e) => set_start_date(e.target.value)}
                  disabled={!is_draft}
                  className={`w-full px-3 py-2 border border-line rounded text-sm font-mono ${
                    is_draft ? 'bg-surface' : 'bg-bg-elev text-ink/50'
                  }`}
                />
              </div>
            </div>
          </div>

          {save.error && (
            <div className="mt-4 text-sm text-bad bg-bad-soft px-3 py-2 rounded">
              {friendly_error(save.error)}
            </div>
          )}

          <div className="mt-6 flex justify-end gap-2">
            <button
              type="button"
              onClick={on_close}
              className="px-4 py-2 text-sm border border-line rounded hover:bg-bg-elev"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={save.isPending}
              className="px-4 py-2 text-sm bg-primary hover:bg-primary-2 disabled:bg-ink/20 text-white rounded"
            >
              {save.isPending ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── AddSubscriberInlineModal ─────────────────────────────────────────────

function AddSubscriberInlineModal({
  chit_id,
  cycle_index,
  on_close,
}: {
  chit_id: string;
  cycle_index: number;
  on_close: () => void;
}): ReactElement {
  const qc = useQueryClient();
  const [mode, set_mode] = useState<'existing' | 'new'>('existing');

  // New-subscriber form state.
  const [name_ta, set_name_ta] = useState('');
  const [name_en, set_name_en] = useState('');
  const [phone, set_phone] = useState('+91');
  const [email, set_email] = useState('');
  const [note, set_note] = useState('');

  // Existing-subscriber picker state.
  const [search_q, set_search_q] = useState('');
  const [picked_id, set_picked_id] = useState<string | null>(null);
  const [picked_label, set_picked_label] = useState<string>('');
  const search_results = useQuery({
    queryKey: ['chit-add-existing', search_q],
    queryFn: () => orpc.reports.subscriber_search({ q: search_q }),
    enabled: mode === 'existing' && search_q.trim().length >= 2,
  });
  const matches = (search_results.data?.subscribers ?? []) as Array<{
    id: string;
    name_ta: string;
    name_en: string;
    phone: string;
    email: string | null;
  }>;

  // Common invalidate-then-close on success.
  const refetch_and_close = async (): Promise<void> => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['cycle-plan', chit_id] }),
      qc.invalidateQueries({ queryKey: ['memberships', chit_id] }),
      qc.invalidateQueries({ queryKey: ['ledger-matrix', chit_id] }),
    ]);
    on_close();
  };

  // Create-new path (legacy).
  const add = useMutation({
    mutationFn: (opts: { replace_existing?: boolean } = {}) =>
      orpc.chits.add_subscriber_to_cycle({
        chit_id,
        cycle_index,
        name_ta,
        name_en,
        phone,
        ...(email ? { email } : {}),
        ...(note ? { notes: note } : {}),
        ...(opts.replace_existing ? { replace_existing: true } : {}),
      }),
    onSuccess: refetch_and_close,
  });

  // Pick-existing path. The structured CONFLICT/SLOT_TAKEN error has the same
  // shape as the create-new path, so the same `is_slot_taken_error` + "Replace
  // {name}?" prompt drive both paths.
  const add_existing = useMutation({
    mutationFn: (opts: { replace_existing?: boolean } = {}) =>
      orpc.chits.add_existing_subscriber_to_cycle({
        chit_id,
        cycle_index,
        subscriber_id: picked_id ?? '',
        ...(opts.replace_existing ? { replace_existing: true } : {}),
      }),
    onSuccess: refetch_and_close,
  });

  const active_mutation = mode === 'existing' ? add_existing : add;
  const slot_taken = is_slot_taken_error(active_mutation.error);

  function submit(e: React.FormEvent): void {
    e.preventDefault();
    if (mode === 'existing') {
      if (!picked_id) return;
      add_existing.mutate({});
    } else {
      add.mutate({});
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={on_close}>
      <div
        className="bg-surface border border-line rounded-lg max-w-xl w-full p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-medium">Add member to cycle {cycle_index}</h2>
        <p className="text-xs text-ink/60 mt-1 mb-4">
          Pick an existing member (from this or another chit) or create a new one.
          The same person can be in many chits at once.
        </p>

        {/* Mode toggle */}
        <div className="flex gap-1 mb-4 p-1 bg-bg-elev rounded">
          <button
            type="button"
            onClick={() => set_mode('existing')}
            className={
              'flex-1 px-3 py-1.5 text-sm rounded transition-colors ' +
              (mode === 'existing'
                ? 'bg-surface font-medium shadow-sm'
                : 'text-ink/60 hover:text-ink')
            }
          >
            Pick existing member
          </button>
          <button
            type="button"
            onClick={() => set_mode('new')}
            className={
              'flex-1 px-3 py-1.5 text-sm rounded transition-colors ' +
              (mode === 'new'
                ? 'bg-surface font-medium shadow-sm'
                : 'text-ink/60 hover:text-ink')
            }
          >
            + Create new member
          </button>
        </div>

        <form onSubmit={submit} className="space-y-3">
          {mode === 'existing' && (
            <>
              <div>
                <label className="block text-xs uppercase tracking-wider text-ink/50 mb-1">
                  Search by name or phone
                </label>
                <input
                  type="search"
                  value={search_q}
                  onChange={(e) => {
                    set_search_q(e.target.value);
                    if (picked_id) {
                      set_picked_id(null);
                      set_picked_label('');
                    }
                  }}
                  placeholder="e.g. Vimal or 9738122401"
                  autoFocus
                  className="w-full px-3 py-2 bg-surface border border-line rounded text-sm"
                />
                <p className="text-[11px] text-ink/50 mt-1">
                  Searches all members of this tenant — including those already in other chits.
                </p>
              </div>

              {picked_id ? (
                <div className="border border-primary bg-primary-soft px-4 py-3 rounded flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium">{picked_label}</div>
                    <div className="text-[11px] text-ink/60 font-mono">{picked_id}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      set_picked_id(null);
                      set_picked_label('');
                    }}
                    className="text-xs text-ink/60 hover:text-bad"
                  >
                    Change
                  </button>
                </div>
              ) : search_q.trim().length >= 2 ? (
                <div className="border border-line rounded max-h-64 overflow-y-auto divide-y divide-line">
                  {search_results.isLoading && (
                    <div className="p-3 text-xs text-ink/60">Searching…</div>
                  )}
                  {!search_results.isLoading && matches.length === 0 && (
                    <div className="p-3 text-xs text-ink/60">
                      No matches. Try a different search, or use "+ Create new member".
                    </div>
                  )}
                  {matches.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => {
                        set_picked_id(m.id);
                        set_picked_label(`${m.name_en || m.name_ta} · ${m.phone}`);
                      }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-bg-elev flex justify-between items-center"
                    >
                      <span>{m.name_en || m.name_ta}</span>
                      <span className="text-[11px] text-ink/60 font-mono">{m.phone}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-ink/40 italic">
                  Type at least 2 characters to search.
                </div>
              )}
            </>
          )}

          {mode === 'new' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-ink/50 mb-1">Name (Tamil)</label>
                  <input
                    type="text"
                    value={name_ta}
                    onChange={(e) => set_name_ta(e.target.value)}
                    required
                    minLength={1}
                    maxLength={120}
                    className="w-full px-3 py-2 bg-surface border border-line rounded text-sm font-tamil"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-ink/50 mb-1">Name (English)</label>
                  <input
                    type="text"
                    value={name_en}
                    onChange={(e) => set_name_en(e.target.value)}
                    required
                    minLength={1}
                    maxLength={120}
                    className="w-full px-3 py-2 bg-surface border border-line rounded text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-ink/50 mb-1">Phone</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => set_phone(e.target.value)}
                  required
                  pattern="^\+[1-9]\d{6,14}$"
                  placeholder="+919876543210"
                  className="w-full px-3 py-2 bg-surface border border-line rounded text-sm font-mono"
                />
                <p className="text-[11px] text-ink/50 mt-1">
                  If this phone matches an existing subscriber, that subscriber is reused (no duplicate).
                </p>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-ink/50 mb-1">
                  Email <span className="text-ink/40">(optional)</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => set_email(e.target.value)}
                  className="w-full px-3 py-2 bg-surface border border-line rounded text-sm"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider text-ink/50 mb-1">
                  Note <span className="text-ink/40">(optional)</span>
                </label>
                <textarea
                  value={note}
                  onChange={(e) => set_note(e.target.value)}
                  rows={3}
                  maxLength={500}
                  className="w-full px-3 py-2 bg-surface border border-line rounded text-sm"
                />
              </div>
            </>
          )}

          {slot_taken ? (
            <div className="border border-warn bg-warn-soft px-4 py-3 rounded space-y-2">
              <p className="text-sm">
                Position <strong>{slot_taken.cycle_index}</strong> is currently filled by{' '}
                <strong>{slot_taken.current_recipient_name}</strong>.
              </p>
              <p className="text-xs text-ink/70">
                Replace them with {mode === 'existing' ? 'the picked member' : 'this new subscriber'}?{' '}
                {slot_taken.current_recipient_name}'s membership will be marked substituted
                (audit trail kept).
              </p>
              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={on_close}
                  className="px-3 py-1.5 text-sm border border-line rounded hover:bg-bg-elev"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={active_mutation.isPending}
                  onClick={() => active_mutation.mutate({ replace_existing: true })}
                  className="px-3 py-1.5 text-sm bg-bad hover:bg-bad/90 disabled:bg-ink/20 text-white rounded"
                >
                  {active_mutation.isPending ? 'Replacing…' : `Replace ${slot_taken.current_recipient_name}`}
                </button>
              </div>
            </div>
          ) : active_mutation.error ? (
            <div className="text-sm text-bad bg-bad-soft px-3 py-2 rounded">
              {friendly_error(active_mutation.error)}
            </div>
          ) : null}

          {!slot_taken && (
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={on_close}
                className="px-4 py-2 text-sm border border-line rounded hover:bg-bg-elev"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={
                  active_mutation.isPending || (mode === 'existing' && !picked_id)
                }
                className="px-4 py-2 text-sm bg-primary hover:bg-primary-2 disabled:bg-ink/20 text-white rounded"
              >
                {active_mutation.isPending
                  ? 'Adding…'
                  : mode === 'existing'
                    ? 'Add to this chit'
                    : 'Create & add'}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

// WhatsApp group for this chit. Only shown when the tenant sends from their own
// number (OpenWA provider + a connected session). Create makes a group named
// after the chit and adds every member; a small composer messages the group.
function ChitWhatsAppGroup({
  chit_id,
  lang,
  is_admin,
}: {
  chit_id: string;
  lang: string;
  is_admin: boolean;
}): ReactElement | null {
  const t = (ta: string, en: string): string => (lang === 'ta' ? ta : en);
  const qc = useQueryClient();
  const [msg, set_msg] = useState('');

  const prov_q = useQuery({ queryKey: ['wa-provider'], queryFn: () => orpc.whatsapp.provider_get({}) });
  const on_openwa = prov_q.data?.provider === 'openwa' && (prov_q.data?.connected ?? false);

  const chit_q = useQuery({
    queryKey: ['chit-wa', chit_id],
    queryFn: () => orpc.chits.get({ chit_id }),
    enabled: !!chit_id && on_openwa,
  });

  const create_m = useMutation({
    mutationFn: () => orpc.whatsapp.create_group({ chit_id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['chit-wa', chit_id] }),
  });
  const send_m = useMutation({
    mutationFn: (body: string) => orpc.whatsapp.send_group({ chit_id, body }),
    onSuccess: () => set_msg(''),
  });

  if (!on_openwa) return null; // group features are OpenWA-only

  const chit = (chit_q.data?.chit ?? {}) as {
    wa_group_id?: string | null;
    wa_group_invite_link?: string | null;
  };
  const has_group = Boolean(chit.wa_group_id);

  return (
    <div className="mb-3 border border-line rounded p-3 bg-surface">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs font-medium">📱 {t('வாட்ஸ்அப் குழு', 'WhatsApp group')}</div>
        {!has_group
          ? is_admin && (
              <button className="btn" onClick={() => create_m.mutate()} disabled={create_m.isPending}>
                {create_m.isPending ? '…' : t('குழுவை உருவாக்கி உறுப்பினர்களைச் சேர்', 'Create group + add members')}
              </button>
            )
          : chit.wa_group_invite_link && (
              <a
                href={chit.wa_group_invite_link}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-primary underline"
              >
                {t('குழு இணைப்பு', 'Group invite link')}
              </a>
            )}
      </div>
      {create_m.error && <div className="text-[11px] text-bad mt-1">{friendly_error(create_m.error)}</div>}
      {has_group && is_admin && (
        <div className="flex items-center gap-2 mt-2">
          <input
            value={msg}
            onChange={(e) => set_msg(e.target.value)}
            placeholder={t('குழுவிற்கு செய்தி…', 'Message the group…')}
            className="flex-1 px-2 py-1 border border-line rounded text-xs"
          />
          <button
            className="btn"
            disabled={send_m.isPending || msg.trim().length === 0}
            onClick={() => send_m.mutate(msg.trim())}
          >
            {send_m.isPending ? '…' : t('அனுப்பு', 'Send')}
          </button>
        </div>
      )}
      {send_m.error && <div className="text-[11px] text-bad mt-1">{friendly_error(send_m.error)}</div>}
    </div>
  );
}
